"use client";
import { FormattedMessage, useIntl } from "services";
import { BackNav, LoadingDisplay, Panel } from "components";
import { useState } from "react";
import { CounselingLayout } from "../components/CounselingLayout";
import { CounselingButton } from "../components/CounselingButton";
import { counselingStatusToPathMap } from "types";
import { useCounselingSubmit } from "hooks";
import { RequestOnsiteSurvey } from "models/RequestOnsiteSurvey";
import { useOrder } from "store";
import {
  ServiceSubtype,
  useCreateSurveyAppointmentMutation,
  useSetSelfGuidedSurveyServiceMutation,
} from "__generated__/types";
import { ApolloError } from "@apollo/client";
import { useNotify } from "services/notification";
import { useSkipSurveyScheduleOnboarding } from "hooks/useSkipSurveyScheduleOnboarding";
import { useSurveyAvailabilities } from "hooks/useSurveyAvailabilities";
import { useFlags } from "services/launch-darkly";
import { datadogRum } from "@datadog/browser-rum";

export function SurveyType() {
  const { formatMessage } = useIntl();
  const order = useOrder();
  const flags = useFlags();
  const [selectedScheduleType, setSelectedScheduleType] =
    useState<ServiceSubtype>(
      flags.enableSelfGuidedSurvey
        ? ServiceSubtype.SelfGuided
        : ServiceSubtype.Virtual
    );

  const {
    surveyAvailabilitiesData,
    surveyServiceData,
    loading: surveyAvailabilitiesLoading,
  } = useSurveyAvailabilities();

  const isVirtualSurveyAvailable = !!(
    surveyAvailabilitiesData?.surveyAvailabilities?.nodes &&
    surveyAvailabilitiesData?.surveyAvailabilities?.nodes.length > 0
  );

  const [createSurveyAppointment] = useCreateSurveyAppointmentMutation();
  const [setSelfGuidedSurvey, { loading: selfGuidedLoading }] =
    useSetSelfGuidedSurveyServiceMutation();
  const notify = useNotify();

  const getNextStatus = () => {
    if (!surveyServiceData?.pendingSurveyService) {
      return "COMPLETE";
    }

    if (selectedScheduleType === "VIRTUAL") {
      return isVirtualSurveyAvailable ? "SURVEY_SCHEDULE_TIME" : "COMPLETE";
    }

    return "COMPLETE";
  };

  const { handleNext, loading } = useCounselingSubmit({
    nextStatus: getNextStatus(),
    saveCallback: async () => {
      if (!surveyServiceData?.pendingSurveyService) {
        return true;
      }

      switch (selectedScheduleType) {
        case ServiceSubtype.Onsite:
          const req = new RequestOnsiteSurvey({ order_id: order.id });
          try {
            await req.save();
            return true;
          } catch (err) {
            return false;
          }
        case ServiceSubtype.SelfGuided:
          await setSelfGuidedSurvey({
            variables: {
              input: { id: surveyServiceData.pendingSurveyService.id },
            },
            onError: (error: ApolloError) => {
              {
                notify("Error setting survey type. Please try again later.", {
                  variant: "error",
                });
                datadogRum.addError(error);
              }
            },
          });
        case ServiceSubtype.Virtual:
          if (!isVirtualSurveyAvailable) {
            await createSurveyAppointment({
              variables: {
                input: {
                  surveyServiceId: surveyServiceData?.pendingSurveyService?.id,
                  surveyAvailabilityId: null,
                },
              },
              onError: (error: ApolloError) => {
                // If user has already scheduled an appointment, show a warning message and take them to the move page.
                // Otherwise, throw the error
                if (
                  error.graphQLErrors[0].extensions?.code ===
                  "appointment_exists"
                ) {
                  notify(
                    formatMessage({
                      id: "counseling.surveyType.message.appointmentExists",
                    }),
                    {
                      variant: "warning",
                    }
                  );
                } else {
                  throw error;
                }
              },
            });
          }
        default:
          return true;
      }
    },
  });

  const loadingSkippingFlag = useSkipSurveyScheduleOnboarding(handleNext);

  return (
    <CounselingLayout ariaLabel={"ariaLabels.counseling.survey.type"}>
      {loadingSkippingFlag ? (
        <LoadingDisplay />
      ) : (
        <>
          <BackNav href={counselingStatusToPathMap.MOVE_DATES} />
          <CounselingLayout.Heading>
            <FormattedMessage id="counseling.surveyType.title" />
          </CounselingLayout.Heading>
          <CounselingLayout.InfoText>
            <FormattedMessage id="counseling.surveyType.instructions" />
          </CounselingLayout.InfoText>
          {flags.enableSelfGuidedSurvey && (
            <Panel
              icon="truck"
              title={
                <FormattedMessage id="counseling.surveyType.selfGuided.title" />
              }
              ariaLabel={formatMessage({
                id: "ariaLabels.counseling.survey.selfGuided",
              })}
              onClick={() => {
                setSelectedScheduleType(ServiceSubtype.SelfGuided);
              }}
              completed={selectedScheduleType === ServiceSubtype.SelfGuided}
            >
              <FormattedMessage id="counseling.surveyType.selfGuided.description" />
            </Panel>
          )}
          <Panel
            icon="boxes_and_lamp"
            title={
              <FormattedMessage id="counseling.surveyType.virtual.title" />
            }
            ariaLabel={formatMessage({
              id: "ariaLabels.counseling.survey.virtual",
            })}
            onClick={() => {
              setSelectedScheduleType(ServiceSubtype.Virtual);
            }}
            completed={selectedScheduleType === ServiceSubtype.Virtual}
          >
            <FormattedMessage id="counseling.surveyType.virtual.description" />
          </Panel>
          <Panel
            icon="couch"
            title={<FormattedMessage id="counseling.surveyType.onsite.title" />}
            ariaLabel={formatMessage({
              id: "ariaLabels.counseling.survey.onsite",
            })}
            onClick={() => {
              setSelectedScheduleType(ServiceSubtype.Onsite);
            }}
            completed={selectedScheduleType === ServiceSubtype.Onsite}
          >
            <FormattedMessage id="counseling.surveyType.onsite.description" />
          </Panel>
          <CounselingButton
            onClick={handleNext}
            isSubmitting={loading}
            disabled={surveyAvailabilitiesLoading || selfGuidedLoading}
          />
        </>
      )}
    </CounselingLayout>
  );
}
